import _ from 'lodash';
import MainWorker from '../webworkers/main.worker.js';
import {openDB, deleteDB, wrap, unwrap} from 'idb';
import axios from 'axios';
import env from '../util/env';
import qs from 'query-string';
import {MsalService} from '@ey/voltron-core';
/**
 * Sync: Syncing the changes done in offline when the app is connected back online
 * Redux Middleware for stating Sync
 * All task related offline changes are tracked in OFFLINE_DB in indexedDB
 * All Document related changes are tracked by DocHelper
 * On Sync, All document related updates need to be synced.
 * Ex: For document upload, document has to be uploaded first before task document association is done
 * In the above example, document upload is tracked by docHelper, while task document association is in OFFLINE_DB
 */
const offlineSync = (config) => (store) => (next) => (action) => {
	const START_OFFLINE_MONITOR = 'START_OFFLINE_MONITOR',
		UPDATE_APP_ONLINE_STATE = 'UPDATE_APP_ONLINE_STATE',
		START_OFFLINE_SYNC = 'START_OFFLINE_SYNC';

	if (action.type != START_OFFLINE_SYNC) {
		return next(action);
	}

	//Check for config availability
	if (!config) {
		const configMsg = `
        Config unavailable.
        The AD package requires config to get the authorization token for the api call.`;
		console.log(configMsg);
	}
	console.log('STARTING SYNC ... ');

	//TODO: Move the indexedDB methods to utility module.
	const ONLINE_DB = 'ONLINE_DB',
		OFFLINE_DB = 'OFFLINE_DB';

	const dbPromise = openDB('canvas-idb', 1, {
		upgrade(db) {
			db.createObjectStore(ONLINE_DB);
			db.createObjectStore(OFFLINE_DB);
		}
	});

	const idbKeyval = {
		async get(key) {
			return (await dbPromise).get(ONLINE_DB, key);
		},
		async getOffline(key) {
			return (await dbPromise).get(OFFLINE_DB, key);
		},
		async getAllOffline() {
			return (await dbPromise).getAll(OFFLINE_DB);
		},
		async set(key, val) {
			return (await dbPromise).put(ONLINE_DB, val, key);
		},
		async setOffline(key, val) {
			return (await dbPromise).put(OFFLINE_DB, val, key);
		},
		async delete(key) {
			return (await dbPromise).delete(ONLINE_DB, key);
		},
		async clear() {
			return (await dbPromise).clear(ONLINE_DB);
		},
		async keys() {
			return (await dbPromise).getAllKeys(ONLINE_DB);
		}
	};

	idbKeyval.getOffline('sync_keys').then((keys) => {
		if (keys && keys.length > 0) {
			next({
				type: 'SET_SYNC_STATE',
				syncState: true
			});
			sessionStorage.setItem('syncItems', keys.length);
		}
	});

	// EngagementID and Token are required for the docHelper related transactions
	const engagementId = qs.parse(window.location.search.toLowerCase()).engagementid;
	/**
	 * Silently acquire an access token for a given set of scopes.
	 * This Will use cached token if available
	 * @returns {token} token
	 */
	async function getAccessToken() {
		// Default scope required to get access token for doc helper api resource
		const request = {resourceUri: config.auth.clientId};
		let token = (await new MsalService(config).getAccessToken(request)).accessToken;
		return token;
	}

	// This PUT call will let DocHelper start the SyncProcess.
	const startDocHelperSyncProcess = function(token) {
		return axios({
			method: 'PUT',
			url: `${action.docHelperConfig.docHelperApiUri}SyncProcessState?state=true`,
			headers: {
				'Content-type': 'application/json',
				isSyncRequest: 'true',
				engagementid: engagementId,
				datasource: 'primary',
				sourceEngagementId: engagementId,
				Authorization: `Bearer ${token}`,
				transactiontype: 'add-edit-evidence'
			}
		});
	};

	// Once we start the docHelper Sync Process, We need to Poll for the status
	// This polling will let us know if the Sync is completed.
	// on sync complete the header for the GET call would have the isSyncComplete to true
	// Currently the polling is set to every 5sec.
	const getDocHelperSyncStatus = function(token) {
		return new Promise((resolve, reject) => {
			const pollStatus = function() {
				console.log('Stating PollStatus call ➰➰');
				axios({
					method: 'GET',
					url: `${action.docHelperConfig.docHelperApiUri}SyncDocument?engagementIds=${engagementId}`,
					headers: {
						'Content-type': 'application/json',
						isSyncRequest: 'true',
						engagementid: engagementId,
						datasource: 'primary',
						sourceEngagementId: engagementId,
						Authorization: `Bearer ${token}`,
						transactiontype: 'add-edit-evidence'
					}
				})
					.then((f) => {
						console.log(
							'Poll Status response: JSON.parse(f.headers.issynccomplete) ➰➰ ',
							f,
							JSON.parse(f.headers.issynccomplete)
						);
						if (JSON.parse(f.headers.issynccomplete)) {
							resolve(f);
						} else {
							setTimeout(pollStatus, 4000);
						}
					})
					.catch((e) => {
						reject(e);
					});
			};
			pollStatus();
		});
	};

	// Once the issynccomplete status is true, the app should tell docHelper that sync is complete
	// The blow PUT call will set the isComplete to true
	const setDocHelperSyncComplete = function(token) {
		return axios({
			method: 'PUT',
			url: `${action.docHelperConfig.docHelperApiUri}SyncDocument?isComplete=true&engagementIds=${engagementId}`,
			headers: {
				'Content-type': 'application/json',
				isSyncRequest: 'true',
				engagementid: engagementId,
				datasource: 'primary',
				sourceEngagementId: engagementId,
				Authorization: `Bearer ${token}`,
				transactiontype: 'add-edit-evidence'
			}
		});
	};

	// Call Dochelper to start Sync, by first setting the onlinestate to true
	// All the methods called in the the will return a promise, gicing them the ability ot chain.
	// Once decHelper sync is completed, start the offline task related
	let token = '';
	axios({
		method: 'PUT',
		url: `${
			action.docHelperConfig.docHelperApiUri
		}onlinestate?state=true&docUrl=${env.getDocServiceUrl()}&resourceUri=${env.getURL('Engg_AuthAudience_URL')}`
	})
		.then(async () => {
			token = await getAccessToken();
		})
		.then(() => startDocHelperSyncProcess(token))
		.then(() => getDocHelperSyncStatus(token))
		.then(() => setDocHelperSyncComplete(token))
		.then(async () => {
			//Since getAll api for indexedDB is not working for Edge.
			var ks = await idbKeyval.getOffline('sync_keys');
			if (ks) {
				ks.forEach((k) => {
					idbKeyval.getOffline(k).then((item) => {
						if (_.isArray(item)) {
							item.forEach((a) => {
								a.meta.forOffline = true;
								a.meta.sync = true;
								next(a);
							});
						} else {
							item.meta.forOffline = true;
							item.meta.sync = true;
							next(item);
						}
						next(action);
					});
				});
			}
			// idbKeyval.getAllOffline().then((d) => {
			// 	console.log('getALL from offline DB:', d);
			// 	if (d && d.length > 0) {
			// 		d.forEach((item, index, array) => {
			// 			if (_.isArray(item)) {
			// 				item.forEach((a) => {
			// 					a.meta.sync = true;
			// 					next(a);
			// 				});
			// 			} else {
			// 				item.meta.sync = true;
			// 				next(item);
			// 			}
			// 		});
			// 		return;
			// 	}
			// 	next(action);
			// });
		})
		.catch((e) => {
			console.log('Failed to set DocHelper Init', e);
		});

	return next(action);
};

export default offlineSync;
